import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import ProductAdminList from "@/pages/admin/products/components/ProductAdminList";
import ProductFormModal from "@/pages/admin/products/components/ProductFormModal";
import CategoryManager from "@/pages/admin/products/components/CategoryManager";
import {
  useAdminProducts,
  type AdminProduct,
  type ProductCategory,
  type ProductCategoryDraft,
  type ProductDraft,
} from "@/pages/admin/products/hooks/useAdminProducts";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function ProductsAdminPage() {
  const {
    products,
    categories,
    loading,
    error,
    reload,
    saveProduct,
    setStatus,
    batchSetStatus,
    removeProduct,
    saveCategory,
    removeCategory,
  } = useAdminProducts();
  const [editing, setEditing] = useState<AdminProduct | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (product: AdminProduct) => {
    setEditing(product);
    setFormOpen(true);
  };

  const handleSubmit = useCallback(
    async (draft: ProductDraft) => {
      await saveProduct(draft);
    },
    [saveProduct],
  );

  const handleToggleStatus = useCallback(
    async (product: AdminProduct) => {
      const next = product.status === "active" ? "inactive" : "active";
      try {
        await setStatus(product.id, next);
        notify(next === "active" ? `已将「${product.name}」上架` : `已将「${product.name}」下架`, "success");
      } catch (err) {
        notify(err instanceof Error ? err.message : "操作失败", "error");
      }
    },
    [setStatus, notify],
  );

  const handleBatchStatus = useCallback(
    async (ids: number[], status: "active" | "inactive") => {
      try {
        const updated = await batchSetStatus(ids, status);
        notify(status === "active" ? `已批量上架 ${updated} 款产品` : `已批量下架 ${updated} 款产品`, "success");
      } catch (err) {
        notify(err instanceof Error ? err.message : "批量操作失败", "error");
      }
    },
    [batchSetStatus, notify],
  );

  const handleDelete = useCallback(
    async (product: AdminProduct) => {
      try {
        await removeProduct(product.id);
        notify(`已删除「${product.name}」`, "success");
      } catch (err) {
        notify(err instanceof Error ? err.message : "删除失败", "error");
      }
    },
    [removeProduct, notify],
  );

  const handleSaveCategory = useCallback(
    async (draft: ProductCategoryDraft) => {
      await saveCategory(draft);
    },
    [saveCategory],
  );

  const handleDeleteCategory = useCallback(
    async (category: ProductCategory) => {
      await removeCategory(category.id);
    },
    [removeCategory],
  );

  const handleFilterByCategory = useCallback((name: string) => {
    setCategoryFilter(name);
    window.setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const productsCountByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    products.forEach((product) => {
      if (product.category) map[product.category] = (map[product.category] ?? 0) + 1;
    });
    return map;
  }, [products]);

  const activeCount = products.filter((product) => product.status === "active").length;
  const draftCount = products.filter((product) => product.status !== "active").length;

  const cards = [
    { label: "产品总数", value: products.length, icon: "ri-apps-2-line", tone: "primary" },
    { label: "已上架", value: activeCount, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "未上架 / 草稿", value: draftCount, icon: "ri-file-edit-line", tone: "accent" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                管理后台
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">软件产品管理</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-apps-2-line" />
                  </span>
                  产品目录维护
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  软件产品管理
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  在后台维护可授权软件的目录：新增或编辑产品、维护版本历史与授权类型，并一键上架或下架。上架后的产品会实时出现在客户侧的「软件产品」页面，供客户选择版本提交授权申请。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:w-auto"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-add-line" />
                  </span>
                  新增产品
                </button>
                <Link
                  to="/admin"
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                    <i className="ri-arrow-left-line" />
                  </span>
                  返回管理后台
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重新加载
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                {cards.map((card) => (
                  <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-600">{card.label}</span>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                        <i className={`${card.icon} text-base`} />
                      </span>
                    </div>
                    <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : card.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <CategoryManager
                  categories={categories}
                  productsCountByCategory={productsCountByCategory}
                  activeCategory={categoryFilter}
                  onFilterByCategory={handleFilterByCategory}
                  onSave={handleSaveCategory}
                  onDelete={handleDeleteCategory}
                  notify={notify}
                />
              </div>

              <div className="mt-6" ref={listRef}>
                <ProductAdminList
                  products={products}
                  categories={categories}
                  categoryFilter={categoryFilter}
                  onCategoryChange={setCategoryFilter}
                  loading={loading}
                  onEdit={openEdit}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleDelete}
                  onBatchStatus={handleBatchStatus}
                />
              </div>
            </>
          )}
        </section>
      </main>

      {formOpen && (
        <ProductFormModal
          product={editing}
          categories={categories}
          onClose={() => setFormOpen(false)}
          onSubmit={handleSubmit}
          notify={notify}
        />
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}